"use client";

import { useMemo } from "react";
import { addDays, format, isSameDay, startOfDay } from "date-fns";
import { pl } from "date-fns/locale";
import { startOfThisWeek } from "@/lib/utils/dates";
import { isChoreActiveToday } from "@/lib/utils/chores";
import { useChores } from "./useChores";
import { useChildMembers } from "./useMembers";
import { useChoreProposals } from "./useChoreProposals";
import type { Chore, ChoreProposal, Member } from "@/lib/schemas";

export type PlanDay = {
  /** Dzień tygodnia (0=PN ... 6=ND, prezentacyjnie). */
  index: number;
  date: Date;
  /** Skrót: PN, WT, ŚR, CZ, PT, SO, ND. */
  short: string;
  /** Numer dnia w miesiącu. */
  num: number;
  isToday: boolean;
  /** Czy dzień już minął (strictly < dzisiaj). UI używa do wyszarzenia
      i blokady kliknięcia — nie planujemy w przeszłości. */
  isPast: boolean;
  /** Aktywne chores zaplanowane na ten dzień (do listy w expand panelu). */
  chores: Chore[];
  /** Liczba aktywnych chores — alias dla chores.length, dla wygody UI. */
  choreCount: number;
};

export type SundayPlan = {
  loading: boolean;
  /** Dzieci do listy "Razem z X i Y" w hero. */
  children: Member[];
  /** Tablica 7 dni od poniedziałku do niedzieli wybranego tygodnia. */
  days: PlanDay[];
  /** Pending proposals — input do "Propozycje od dzieci". */
  proposals: ChoreProposal[];
  /** Poniedziałek wybranego tygodnia (do nawigacji prev/next). */
  weekStart: Date;
  /** Etykieta zakresu np. "4 – 10 maja". */
  weekLabel: string;
  /** Czy wybrany tydzień jest aktualnym tygodniem (do labelek "Ten tydzień"). */
  isCurrentWeek: boolean;
};

const DAY_SHORT = ["PN", "WT", "ŚR", "CZ", "PT", "SO", "ND"];

/**
 * @param weekStart Opcjonalny poniedziałek tygodnia, dla którego budujemy plan.
 *                  Domyślnie aktualny tydzień. Pozwala UI nawigować w przód/wstecz.
 */
export function useSundayPlan(
  householdId: string | undefined,
  weekStart?: Date,
): SundayPlan {
  const { chores, loading: choresLoading } = useChores(householdId);
  const { members: children, loading: childrenLoading } = useChildMembers(householdId);
  const { proposals, loading: proposalsLoading } = useChoreProposals(
    householdId,
    { status: "pending" },
  );

  // Stabilizuj date — bez tego każdy render produkuje nowy Date i memo nie działa.
  const startMs = weekStart?.getTime();
  const start = useMemo(
    () => (startMs ? new Date(startMs) : startOfThisWeek()),
    [startMs],
  );

  return useMemo(() => {
    const now = new Date();
    const currentWeekStart = startOfThisWeek(now);
    const isCurrentWeek = isSameDay(startOfDay(start), currentWeekStart);

    const todayMid = startOfDay(now);

    const days: PlanDay[] = DAY_SHORT.map((short, i) => {
      const date = addDays(start, i);
      const sameDay = isSameDay(date, now);
      const isPast = startOfDay(date) < todayMid;
      const dayChores = chores.filter(
        (c) => c.active && isChoreActiveToday({ schedule: c.schedule }, date),
      );
      return {
        index: i,
        date,
        short,
        num: Number(format(date, "d", { locale: pl })),
        isToday: sameDay,
        isPast,
        chores: dayChores,
        choreCount: dayChores.length,
      };
    });

    const last = days[6].date;
    const sameMonth = start.getMonth() === last.getMonth();
    const weekLabel = sameMonth
      ? `${start.getDate()} – ${last.getDate()} ${format(last, "LLLL", { locale: pl })}`
      : `${format(start, "d MMM", { locale: pl })} – ${format(last, "d MMM", { locale: pl })}`;

    return {
      loading: choresLoading || childrenLoading || proposalsLoading,
      children,
      days,
      proposals,
      weekStart: start,
      weekLabel,
      isCurrentWeek,
    };
  }, [chores, children, proposals, choresLoading, childrenLoading, proposalsLoading, start]);
}

/**
 * Lista imion dzieci sklejona po polsku: "Iga", "Iga i Leo", "Iga, Leo i Ania".
 * Zwraca pusty string gdy nie ma dzieci.
 */
export function joinChildrenNames(children: Member[]): string {
  const names = children.map((m) => m.displayName);
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} i ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} i ${names[names.length - 1]}`;
}
